import { useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { useAuthenticatedUser } from 'features/authentication';
import { Modal } from 'features/modals';
import { useStore } from 'models/Provider';

export const ExportApplicants = ({ closeModal, query, conjunction, count, totalCount }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { api, t, current } = useStore();
  const { showError } = useToaster();
  const me = useAuthenticatedUser();
  const { useSsnRegistry, isEnterprise } = current.info;

  const headerFields = {
    details: ['id', 'name', 'email', 'phone', ...(useSsnRegistry ? ['ssn', 'age', 'gender'] : [])],
    jobStatus: [
      'job_id',
      'job_title',
      ...(isEnterprise ? ['job_department'] : []),
      'job_location',
      'job_label',
      'date',
      'is_rejected',
      'is_notified_of_rejection',
      'date_of_rejection',
      'is_hired',
      'date_of_hire',
      'is_retracted',
      'is_manually_added',
      'job_stage_name',
      'num_reviews',
      'num_notes',
      'referred_site',
    ],
  };

  const [fields, setFields] = useState(() => ({
    details: { label: t('exportmodal.fields.details'), isHeader: true, checked: false },
    id: { label: t('exportmodal.fields.id'), checked: false },
    name: { label: t('exportmodal.fields.name'), checked: false },
    email: { label: t('exportmodal.fields.email'), checked: false },
    phone: { label: t('exportmodal.fields.phone'), checked: false },
    ...(useSsnRegistry && { ssn: { label: t('exportmodal.fields.ssn'), checked: false } }),
    ...(useSsnRegistry && { age: { label: t('exportmodal.fields.age'), checked: false } }),
    ...(useSsnRegistry && { gender: { label: t('exportmodal.fields.gender'), checked: false } }),
    jobStatus: { label: t('exportmodal.fields.jobstatus'), isHeader: true, checked: false },
    job_id: { label: t('exportmodal.fields.jobid'), checked: false },
    job_title: { label: t('exportmodal.fields.jobtitle'), checked: false },
    ...(isEnterprise && {
      job_department: { label: t('exportmodal.fields.jobdepartment'), checked: false },
    }),
    job_location: { label: t('exportmodal.fields.joblocation'), checked: false },
    job_label: { label: t('exportmodal.fields.joblabel'), checked: false },
    date: { label: t('exportmodal.fields.date'), checked: false },
    is_rejected: { label: t('exportmodal.fields.is_rejected'), checked: false },
    is_notified_of_rejection: {
      label: t('exportmodal.fields.is_notified_of_rejection'),
      checked: false,
    },
    date_of_rejection: { label: t('exportmodal.fields.date_rejected'), checked: false },
    is_hired: { label: t('exportmodal.fields.is_hired'), checked: false },
    date_of_hire: { label: t('exportmodal.fields.date_hired'), checked: false },
    is_retracted: { label: t('exportmodal.fields.is_retracted'), checked: false },
    is_manually_added: { label: t('exportmodal.fields.is_manually_added'), checked: false },
    referred_site: { label: t('exportmodal.fields.referred_site'), checked: false },
    job_stage_name: { label: t('exportmodal.fields.stage_name'), checked: false },
    num_reviews: { label: t('exportmodal.fields.num_reviews'), checked: false },
    num_notes: { label: t('exportmodal.fields.num_notes'), checked: false },
  }));
  const fieldsArray = Object.entries(fields).map((e) => ({ id: e[0], ...e[1] }));
  function addOrRemoveField(fieldId) {
    let newFields = Object.assign({}, fields);
    newFields[fieldId].checked = !newFields[fieldId].checked;
    setFields(newFields);
  }

  let fieldList = [];
  for (let key in fields) {
    const field = fields[key];
    fieldList.push(
      <Checkbox
        key={key}
        label={field.label}
        labelFont={field.isHeader ? 'bold14' : 'regular14'}
        value={field.checked}
        onChange={() => addOrRemoveField(key)}
      />,
    );
  }

  const selectedAllFields = (section) =>
    fieldsArray
      .filter((field) => headerFields[section].includes(field.id))
      .every((field) => field.checked);
  const selectedSomeFields = (section) =>
    fieldsArray
      .filter((field) => headerFields[section].includes(field.id))
      .some((field) => field.checked);

  const selectAllFields = (section) => {
    let newFields = Object.assign({}, fields);
    const checked = selectedAllFields(section);
    headerFields[section].map((key) => (newFields[key].checked = !checked));
    newFields[section].checked = !checked;
    setFields(newFields);
  };

  async function onSubmit() {
    setSubmitting(true);
    let checkedFields = [];
    for (let key in fields) {
      if (fields[key].checked && !fields[key].isHeader) checkedFields.push(key);
    }

    let exportFunction = api.applications.searchExport;
    if (conjunction === 'or') {
      exportFunction = api.applications.orSearchExport;
    }

    const response = await exportFunction(query, {
      fields: checkedFields,
    });
    if (!response.error) {
      setSubmitted(true);
    } else {
      showError(t('exportmodal.submit.error'));
    }
    setSubmitting(false);
  }

  if (submitted) {
    return (
      <Modal>
        <Modal.Header text={t('exportmodal.submitted.title')} />

        <Text
          text={
            <>
              {t('exportmodal.submitted.confirmtext.sent')} <strong>{me.email}</strong>{' '}
              {t('exportmodal.submitted.confirmtext.ready')}
            </>
          }
        />

        <Text text={t('exportmodal.submitted.notice')} />
        <Modal.Actions>
          <Button onClick={closeModal} text={t('exportmodal.button.close')} />
        </Modal.Actions>
      </Modal>
    );
  }
  return (
    <Modal>
      <Modal.Header text={t('exportmodal.title')} />
      <Stack space={1}>
        {count !== totalCount && <Text text={t('exportmodal.exporting.filteredout')} />}
        <Text text={t('exportmodal.exporting.number', count, totalCount)} />
      </Stack>
      <Separator />
      <Stack space={4}>
        <Text font="bold18" text={t('exportmodal.labels.headers')} />
        <Stack space={2}>
          {fieldsArray.map((field) => (
            <Stack space={0} key={field.id}>
              {field.isHeader ? (
                <div key={field.id} style={{ marginTop: '24px' }}>
                  <Checkbox
                    key={field.id}
                    label={field.label}
                    labelFont={'bold14'}
                    value={
                      selectedAllFields(field.id)
                        ? true
                        : selectedSomeFields(field.id)
                          ? 'mixed'
                          : false
                    }
                    onChange={() => selectAllFields(field.id)}
                  />
                </div>
              ) : (
                <Checkbox
                  key={field.id}
                  label={field.label}
                  value={field.checked}
                  onChange={() => addOrRemoveField(field.id)}
                />
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Modal.Actions>
        <Button variant="secondary" onClick={closeModal} text={t('exportmodal.button.cancel')} />
        <Button
          onClick={onSubmit}
          isDisabled={submitting || fieldsArray.filter((field) => field.checked).length === 0}
          text={t('exportmodal.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
};
